import React from 'react';
import { List, Button, Popconfirm, Space, Typography, Empty, App, Descriptions } from 'antd';
import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import { useAddOnPurchase } from '../../hooks/useAddOnPurchase';
import type { AddOnType } from '@shared/config/subscription-addons';
import { calculatePriceWithTax } from '@shared/config/subscription-addons';
import { withDevModalFilePath } from '../common';

const { Text } = Typography;
const formatYen = (value: number) => `${value.toLocaleString()}円`;

type AddOnRemovalViolation = {
  type: 'equipment' | 'storage';
  currentValue: number;
  unit: string;
  newLimit: number;
  excessAmount: number;
};

type AddOnRemovalApiError = {
  message?: string;
  data?: {
    cause?: {
      code?: string;
      violations?: AddOnRemovalViolation[];
    };
  };
};

const isAddOnRemovalApiError = (error: unknown): error is AddOnRemovalApiError =>
  typeof error === 'object' && error !== null;

type SubscriptionAddOnListItem = {
  type?: AddOnType;
  quantity?: number;
  totalPrice?: number;
  startDate?: string | Date;
  unitPrice?: number;
  endDate?: string | Date | null;
};

interface AddonListProps {
  addOns?: SubscriptionAddOnListItem[];
  isManager: boolean;
  hasStripeSubscription: boolean;
  isCancellationPending?: boolean; // 解約申請中かどうか
}

const { Modal } = withDevModalFilePath('client/src/components/company/AddonList.tsx');

export const AddonList: React.FC<AddonListProps> = ({
  addOns,
  isManager,
  hasStripeSubscription,
  isCancellationPending = false,
}) => {
  const { message } = App.useApp();
  const { removeAddOn, isRemovingAddOn } = useAddOnPurchase();

  const handleRemoveAddOn = async (index: number) => {
    try {
      await removeAddOn(
        { index },
        {
          onSuccess: () => {
            message.success('追加オプションを解約しました。');
          },
          onError: (error: unknown) => {
            // バリデーションエラーの詳細表示
            if (
              isAddOnRemovalApiError(error) &&
              error.data?.cause?.code === 'ADDON_REMOVAL_VALIDATION_ERROR'
            ) {
              const violations = error.data.cause?.violations ?? [];

              Modal.error({
                title: (
                  <Space>
                    <WarningOutlined style={{ color: '#ff4d4f' }} />
                    追加オプションを解約できません
                  </Space>
                ),
                width: 600,
                content: (
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Text>
                      現在の使用状況が解約後の上限を超えているため、追加オプションを解約できません。
                      以下の項目を削減してから再度お試しください。
                    </Text>

                    {violations.map((v: AddOnRemovalViolation, idx: number) => (
                      <div key={idx}>
                        <Text strong style={{ fontSize: 14 }}>
                          {v.type === 'equipment' ? '設備数' : 'ストレージ容量'} が超過しています
                        </Text>
                        <Descriptions column={1} size="small" bordered style={{ marginTop: 8 }}>
                          <Descriptions.Item label="現在の使用状況">
                            {v.currentValue} {v.unit}
                          </Descriptions.Item>
                          <Descriptions.Item label="解約後の上限">
                            {v.newLimit} {v.unit}
                          </Descriptions.Item>
                          <Descriptions.Item label="削減が必要な量">
                            <Text type="danger" strong style={{ fontSize: 14 }}>
                              {v.excessAmount} {v.unit}
                            </Text>
                          </Descriptions.Item>
                        </Descriptions>

                        {v.type === 'equipment' && (
                          <Text type="secondary" style={{ marginTop: 8, display: 'block', fontSize: 12 }}>
                            設備を削除するには{' '}
                            <a href="/app/equipments" target="_blank" rel="noopener noreferrer">
                              設備一覧ページ
                            </a>
                            {' '}から不要な設備を削除してください。
                          </Text>
                        )}

                        {v.type === 'storage' && (
                          <Text type="secondary" style={{ marginTop: 8, display: 'block', fontSize: 12 }}>
                            ストレージを削減するには{' '}
                            <a href="/app/settings/storage" target="_blank" rel="noopener noreferrer">
                              ストレージ管理ページ
                            </a>
                            {' '}から不要なファイルを削除してください。
                          </Text>
                        )}
                      </div>
                    ))}
                  </Space>
                ),
              });
              return;
            }

            const errorMessage = error instanceof Error ? error.message : 'エラーが発生しました';
            message.error(`追加オプションの解約に失敗しました: ${errorMessage}`);
          },
        }
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'エラーが発生しました';
      message.error(`エラーが発生しました: ${errorMessage}`);
    }
  };

  if (!addOns || addOns.length === 0) {
    return <Empty description="追加オプションはありません" />;
  }

  return (
    <List<SubscriptionAddOnListItem>
      dataSource={addOns}
      renderItem={(addOn, index) => {
        const quantity = addOn.quantity ?? 0;
        const totalPrice = addOn.totalPrice ?? 0;
        const startDateLabel = addOn.startDate
          ? new Date(addOn.startDate).toLocaleDateString('ja-JP')
          : '-';
        return (
          <List.Item
            actions={
              isManager && hasStripeSubscription && !isCancellationPending
                ? [
                  <Popconfirm
                    key="delete"
                    title="この追加オプションを解約しますか?"
                    description={
                      <div style={{ maxWidth: 300 }}>
                        <Text type="danger" strong>解約すると即座に機能が停止します。</Text>
                        <br />
                        <Text type="secondary">解約後の返金はありません。</Text>
                      </div>
                    }
                    onConfirm={() => handleRemoveAddOn(index)}
                    okText="解約する"
                    cancelText="キャンセル"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="link"
                      danger
                      icon={<DeleteOutlined />}
                      loading={isRemovingAddOn}
                    >
                      解約
                    </Button>
                  </Popconfirm>,
                ]
                : []
            }
          >
            <List.Item.Meta
              title={
                addOn.type === 'equipment'
                  ? `設備数追加: ${quantity}台`
                  : addOn.type === 'storage'
                    ? `ストレージ容量追加: ${quantity}GB`
                    : `追加オプション: ${quantity}`
              }
              description={
                <Space direction="vertical" size="small">
                  <div>
                    <Text strong style={{ fontSize: 14 }}>
                      月額 {formatYen(totalPrice)} /月{' '}
                    </Text>
                    <Text type="secondary" style={{ color: '#999' }}>
                      (税込{formatYen(calculatePriceWithTax(totalPrice))} /月)
                    </Text>
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    開始日: {startDateLabel}
                  </Text>
                </Space>
              }
            />
          </List.Item>
        );
      }}
    />
  );
};

export default AddonList;
